import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { WordStatus, TextDisplayMode } from '@shared/types';
import { animate } from 'animejs';
import { useStores } from '@web/hooks/use-stores.hooks';
import parse from 'html-react-parser';
import { CSSProperties } from 'react';
import { observer } from 'mobx-react-lite';

interface WordElementProps {
  word: string;
  status: WordStatus;
  isCurrentWord: boolean;
  mode: TextDisplayMode;
  errorAttemptId?: number;
  showArrowIndicator?: boolean;
  originalWord?: string;
  attemptedWords?: string[];
  'data-word-index'?: number;
}

interface StyledWordProps {
  status: WordStatus;
  mode: string;
  isCurrentWord: boolean;
}

const StyledWord = styled('span')<StyledWordProps>(
  ({ status, mode, isCurrentWord }) => ({
    position: 'relative',
    display: 'inline-block',
    margin: '0',
    padding: '0 4px',
    borderRadius: '3px',
    transition: 'all 0.3s ease-in-out',
    cursor: 'default',
    // הצבעים והסמן (פס וגוון מאחורי המילה הנוכחית) מגיעים מה-CSS עם הקלאסים
  })
);

const WordElement: React.FC<WordElementProps> = observer(({
  word,
  status,
  isCurrentWord,
  mode,
  errorAttemptId,
  showArrowIndicator = true,
  originalWord,
  attemptedWords,
  'data-word-index': dataWordIndex,
}) => {
  const wordRef = useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();
  const { stores } = useStores();
  // The amber states carry their instruction: a hover or a long press says
  // what the colour means.
  const hint =
    mode === 'preview' ? undefined
    : status === WordStatus.Near ? t('wordNear')
    : status === WordStatus.Coarticulated ? t('wordCoarticulated')
    : undefined;
  const isPaused = stores?.speechRecognitionStore?.isPaused ?? false;
  // The pointer: a tint behind the word to read now and a dark bar under it,
  // like karaoke. It replaced a small blue triangle above the word that was
  // easy to lose. The words still ahead are a little quieter.
  const showPointer = showArrowIndicator && mode !== 'result' && mode !== 'preview';
  const isAhead = mode === 'reading' && status === WordStatus.Pending && !isCurrentWord;

  useEffect(() => {
    if (isCurrentWord && status === WordStatus.Mistake && typeof errorAttemptId !== 'undefined') {
      if (wordRef.current) {
        animate(wordRef.current, {
          scale: [
            { to: 1.25, duration: 100 },
            { to: 0.95, duration: 100 },
            { to: 1, duration: 200 },
          ],
          ease: 'out(2)',
          duration: 400,
        });
      }
    }
  }, [status, word, isCurrentWord, errorAttemptId]);



  return (
    <StyledWord
      className={[
        'word-element',
        mode !== 'preview' ? status : '',
        isCurrentWord ? 'current-word' : '',
        isCurrentWord && showPointer ? 'pointer' : '',
        isCurrentWord && showPointer && isPaused ? 'pointer-paused' : '',
        isAhead ? 'ahead' : '',
      ].filter(Boolean).join(' ')}
      status={status}
      mode={mode}
      isCurrentWord={isCurrentWord}
      data-word-status={mode !== 'preview' ? status : undefined}
      data-word-index={dataWordIndex}
      title={hint}
      >
      <span
        className="word"
        data-clean-word={originalWord || word}
        ref={wordRef}
        style={{
          display: 'inline-block',
          filter:
            (mode === 'byHeart' && status === WordStatus.Pending) ||
            (mode === 'byHeart' && status === WordStatus.Mistake)
              ? 'blur(6px)'
              : 'none',
          opacity: mode === 'byHeart' && status === WordStatus.Pending ? 0.4 : 1,
        }}
      >
        {parse(word)}
      </span>
    </StyledWord>
  );
});

WordElement.displayName = 'WordElement';

export default WordElement;
